import { Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Box, Typography, IconButton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useEffect, useState, useCallback } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ChartDataProps, RefillDataProps, TabPanelProps } from '../../interfaces/Billing';
import { EmptyState } from '../../assets/icons/Index';
import { LoadingButton } from '@mui/lab';
import BuyCreditsModal from '../../components/specific/modal/BuyCreditsModal';
import { AppRoutes } from '../../enums/Approutes';
import { useNavigate } from 'react-router-dom';
import { billingService } from '../../services';
import { useGlobalData } from '../../hooks/globalDataContext';
import { notifyCreditsUpdated } from '../../utils/creditsEvents';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Loader from '../../assets/gifs/loader.gif'

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`billing-tabpanel-${index}`}
            aria-labelledby={`billing-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const Billing = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { credits, refreshCredits } = useGlobalData();
    const [tabValue, setTabValue] = useState<number>(0);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(5);
    const [selectedYear, setSelectedYear] = useState<number>(0);
    const [refillsData, setRefillsData] = useState<RefillDataProps[]>([]);
    const [chartData, setChartData] = useState<ChartDataProps[]>([]);
    const [hasChartData, setHasChartData] = useState<boolean>(false);
    const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState<boolean>(false);
    const [joinedIn, setJoinedIn] = useState<number>(new Date().getFullYear());
    const [isLoadingRefills, setIsLoadingRefills] = useState<boolean>(false);
    const [isLoadingChart, setIsLoadingChart] = useState<boolean>(false);

    // Anno corrente e limite superiore
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear;

    // Inizializza selectedYear con l'anno corrente o joinedIn se disponibile
    useEffect(() => {
        const currentYear = new Date().getFullYear();
        if (joinedIn > 0) {
            setSelectedYear(Math.max(joinedIn, currentYear));
        } else {
            setSelectedYear(currentYear);
        }
    }, [joinedIn]);

    useEffect(() => {
        const fetchRefills = async () => {
            setIsLoadingRefills(true);
            try {
                const data = await billingService.getUserRefills();
                setRefillsData(data.results);
            } catch (error) {
                console.error('Errore durante il recupero delle ricariche:', error);
                setRefillsData([]);
                setIsLoadingRefills(false);
            } finally {
                setIsLoadingRefills(false);
            }
        };

        const fetchDataChart = async () => {
            setIsLoadingChart(true);
            try {
                const response = await billingService.getPaymentsGraph(selectedYear);

                if (response.status === 204) {
                    setChartData([]);
                    setHasChartData(false);
                    setJoinedIn(selectedYear);
                    return;
                }

                const data = response.data;
                setJoinedIn(data.joinedIn);

                const transformedData = [
                    {
                        name: t('billing:pinnalo_ai'),
                        value: data.AI.percentage,
                        color: '#9E9E9E',
                        credits: data.AI.credits
                    },
                    {
                        name: t('billing:pinner_pro'),
                        value: data.consultancy.percentage,
                        color: '#E9495A',
                        credits: data.consultancy.credits
                    },
                ];
                setChartData(transformedData);
                const hasData = transformedData.some(item => item.value > 0);
                setHasChartData(hasData);
            } catch (error) {
                console.error('Errore durante il recupero dei dati:', error);
                setChartData([]);
                setHasChartData(false);
                setIsLoadingChart(false);
            } finally {
                setIsLoadingChart(false);
            }
        };

        fetchRefills();

        if (selectedYear > 0) {
            fetchDataChart();
        }
    }, [selectedYear, t]);

    useEffect(() => {
        const query = new URLSearchParams(window.location.search);
        if (query.get('success') === 'true') {
            navigate(AppRoutes.BILLING_SUCCESS);
        } else if (query.get('canceled') === 'true') {
            navigate(AppRoutes.BILLING_CANCELED);
        }
    }, [navigate]);

    const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    }, []);

    const handleChangePage = useCallback((event: unknown, newPage: number) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    const handleYearChange = useCallback((direction: 'prev' | 'next') => {
        if (direction === 'prev') {
            setSelectedYear(prev => Math.max(prev - 1, joinedIn));
        } else {
            setSelectedYear(prev => Math.min(prev + 1, maxYear));
        }
    }, [maxYear, joinedIn]);

    const formatDate = useCallback((dateString: string) => {
        return new Date(dateString).toLocaleDateString('it-IT');
    }, []);

    const handleOpenBuyCreditsModal = useCallback(() => {
        setIsBuyCreditsModalOpen(true);
    }, []);

    const handleCloseBuyCreditsModal = useCallback(() => {
        setIsBuyCreditsModalOpen(false);
        // Aggiorna i crediti dopo la chiusura del modal
        refreshCredits(true);
        // Emette un evento per notificare altri componenti
        notifyCreditsUpdated();
    }, [refreshCredits]);

    return (
        <div className='max-h-screen w-full px-4 py-8 md:px-[23%] md:py-14'>
            <div className='w-full h-full flex flex-col md:flex-row gap-6 md:gap-10'>
                <div className='w-full md:w-1/3 flex flex-col items-center justify-center rounded-sm shadow-xl p-6'>
                    <h1 className='text-2xl font-bold mb-0'>{credits}</h1>
                    <p className='text-sm text-gray-500'>{t('billing:available_credits')}</p>
                </div>
                <div className='w-full md:w-2/3 flex flex-col justify-around gap-4 md:gap-2'>
                    <p className='text-[0.94rem]'>{t('billing:credits_description')}</p>
                    <div className='flex flex-col md:flex-row gap-4 md:gap-8'>
                        <LoadingButton
                            onClick={handleOpenBuyCreditsModal}
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, backgroundColor: '#E9495A', color: '#FFFFFF', '&:hover': { backgroundColor: '#E6263B' }, '&.Mui-disabled': { backgroundColor: '#E9495A', opacity: 0.5 } }}
                            fullWidth
                        >
                            {t('billing:buyCredits')}
                        </LoadingButton>
                    </div>
                </div>
            </div>

            <div className='mt-8 md:mt-12'>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="billing tabs"
                        TabIndicatorProps={{
                            style: {
                                backgroundColor: '#E9495A',
                            },
                        }}
                    >
                        <Tab
                            label={t('billing:refill_credits')}
                            sx={{
                                '&.Mui-selected': {
                                    color: '#E9495A',
                                },
                            }}
                        />
                        <Tab
                            label={t('billing:usage_credits')}
                            sx={{
                                '&.Mui-selected': {
                                    color: '#E9495A',
                                },
                            }}
                        />
                    </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                    {isLoadingRefills ? (
                        <div className='w-full h-[10vh] my-4 flex justify-center items-center'>
                            <img src={Loader} alt='Loading' style={{ width: '3rem', height: 'auto', objectFit: 'cover' }} />
                        </div>
                    ) : !isLoadingRefills && refillsData.length > 0 ? (
                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E0E0E0' }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
                                        <TableCell sx={{ fontWeight: 'bold', color: '#1A1A1A' }}>{t('billing:date')}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: '#1A1A1A' }}>{t('billing:refill')}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: '#1A1A1A' }}>{t('billing:invoice')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {refillsData
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#F8F9FA' } }}>
                                                <TableCell sx={{ color: '#475467' }}>{formatDate(row.purchased_at)}</TableCell>
                                                <TableCell sx={{ color: '#475467', fontWeight: 'bold' }}>{`${row.amount} ${t('billing:credits')}`}
                                                </TableCell>
                                                <TableCell sx={{ color: '#475467', fontWeight: 'bold' }}>
                                                    <IconButton className='p-0 [&:hover]:bg-transparent' onClick={() => window.open(row.invoice_url, '_blank')}>
                                                        <DescriptionOutlinedIcon sx={{ color: '#1A1A1A' }} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[5]}
                                component="div"
                                count={refillsData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelDisplayedRows={() => `${page + 1}`}
                                showFirstButton={false}
                                showLastButton={false}
                                sx={{
                                    '& .MuiTablePagination-selectLabel': { display: 'none' },
                                    '& .MuiTablePagination-displayedRows': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }
                                }}
                            />
                        </TableContainer>
                    ) : !isLoadingRefills && refillsData.length === 0 ? (
                        <div className='w-full h-[40vh] my-4 flex flex-col justify-center items-center gap-4'>
                            <Typography variant="body1" className="text-text-primary">
                                {t('billing:noRefills_title')}
                            </Typography>
                            <Typography variant="body1" className="text-text-primary text-center mb-2">
                                {t('billing:noRefills_subtitle')}
                            </Typography>
                            <EmptyState className='' />
                        </div>
                    ) : null}
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        {isLoadingChart ? (
                            <div className='w-full h-[10vh] my-4 flex justify-center items-center'>
                                <img src={Loader} alt='Loading' style={{ width: '3rem', height: 'auto', objectFit: 'cover' }} />
                            </div>
                        ) : !isLoadingChart && hasChartData ? (
                            <Box sx={{ width: '100%', height: 300, maxWidth: 400 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={chartData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ credits }) => `${credits || 0} ${t('billing:credits')}`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value: number) => {
                                                return [
                                                    `${value}%`
                                                ];
                                            }}
                                            labelStyle={{ color: '#1A1A1A' }}
                                        />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        ) : !isLoadingChart && !hasChartData ? (
                            <div className='w-full h-[26vh] my-4 flex flex-col justify-center items-center gap-4'>
                                <Typography variant="body1" className="text-text-primary">
                                    {t('billing:noData_title')}
                                </Typography>
                                <Typography variant="body1" className="text-text-primary text-center mb-2">
                                    {t('billing:noData_subtitle')}
                                </Typography>
                                <EmptyState className='' />
                            </div>
                        ) : null}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton
                                onClick={() => handleYearChange('prev')}
                                disabled={selectedYear <= joinedIn}
                                sx={{
                                    opacity: selectedYear <= joinedIn ? 0.5 : 1,
                                    cursor: selectedYear <= joinedIn ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                            <Typography variant="body1" sx={{ color: '#1A1A1A', fontWeight: 'bold' }}>
                                {selectedYear}
                            </Typography>
                            <IconButton
                                onClick={() => handleYearChange('next')}
                                disabled={selectedYear >= maxYear}
                                sx={{
                                    opacity: selectedYear >= maxYear ? 0.5 : 1,
                                    cursor: selectedYear >= maxYear ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </TabPanel>
            </div>

            <BuyCreditsModal
                open={isBuyCreditsModalOpen}
                onClose={handleCloseBuyCreditsModal}
            />
        </div>
    )
}

export default Billing